import { useState } from "react";
import { X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { BookingModalProps } from "./types";
import { useForm, FormProvider } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { BookingFormValues, formSchema } from "./types/overview";
import { Spinner } from "components"; // Assuming you have a Spinner component
import { useMutation, useQueryClient } from "react-query";
import { updateProducts } from "./apis";
import { useParams } from "react-router-dom";
import { toast } from "sonner";

const reasonOptions = ["Stock unavailable", "No delivery person", "Others"];

const ConfirmBookingModal = ({ onClose, onSubmit, initialData }: BookingModalProps) => {
  const [selectedTab, setSelectedTab] = useState(initialData[0]?.id);
  const [rejectedGroups, setRejectedGroups] = useState<Set<string>>(new Set());
  const queryClient = useQueryClient();
  const {id} = useParams();

  // Transform initial data into form state
  const transformedInitialData = {
    order_id: Number(id),
    status: initialData.flatMap(customer =>
      customer.product_detail.map(product => ({
        id: product.id.toString(),
        name: product.name,
        reason_for_reject: "",
        status: "Accept" as const
      }))
    )
  };

  const methods = useForm<BookingFormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: transformedInitialData
  });

  const { handleSubmit, watch, setValue, getValues } = methods;

  // Get current form values
  const formValues = watch();

  const submitMutation = useMutation({
    mutationFn: updateProducts,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['orderDetails', transformedInitialData.order_id.toString()] });
      
      if (onSubmit) {
        onSubmit(data);
      }
      
      onClose();
      toast.success("Booking confirmation submitted successfully!");
    },
    onError: (error) => {
      console.error('Error submitting booking confirmation:', error);
    }
  });

  // Handle status change
  const handleStatusChange = (productId: string, newStatus: "Accept" | "Reject") => {
    const statusIndex = formValues.status.findIndex(item => item.id === productId);
    if (statusIndex !== -1) {
      // Update status
      setValue(`status.${statusIndex}.status`, newStatus);

      // Clear reason if accepted
      if (newStatus === "Accept") {
        setValue(`status.${statusIndex}.reason_for_reject`, "");
      }

      // Update rejected groups for UI
      updateRejectedGroups();
    }
  };

  // Update rejected groups based on current form values
  const updateRejectedGroups = () => {
    const currentValues = getValues().status;
    const newRejectedGroups = new Set<string>();

    currentValues.forEach(item => {
      if (item.status === "Reject") {
        const productName = item.id; // Extract product name from ID
        newRejectedGroups.add(productName);
      }
    });

    setRejectedGroups(newRejectedGroups);
  };

  // Handle reason tag selection
  const handleReasonTag = (productIds: string[], tag: string) => {
    productIds.forEach(id => {
      const statusIndex = formValues.status.findIndex(item => item.id === id);
      if (statusIndex !== -1) {
        setValue(`status.${statusIndex}.reason_for_reject`, tag);
      }
    });
  };

  // Handle reason text input
  const handleReasonText = (productIds: string[], text: string) => {
    productIds.forEach(id => {
      const statusIndex = formValues.status.findIndex(item => item.id === id);
      if (statusIndex !== -1) {
        setValue(`status.${statusIndex}.reason_for_reject`, text);
      }
    });
  };

  // Mark all as accepted
  const markAllAccepted = () => {
    formValues.status.forEach((_, index) => {
      setValue(`status.${index}.status`, "Accept");
      setValue(`status.${index}.reason_for_reject`, "");
    });
    setRejectedGroups(new Set());
  };

  // only selected customer tab rejectedGroup
  const selectedCustomer = initialData.find(c => c.id === selectedTab);
  const selectedCustomerRejectedGroups = Array.from(rejectedGroups).filter(group =>
    selectedCustomer?.product_detail.some(product => product.id.toString() === group)
  );

  // Process form submission with React Query
  const onFormSubmit = (data: BookingFormValues) => {
    const dataToSend = {
      order_id: data?.order_id,
      status: data.status.map(item => ({
        id: item.id,
        reason_for_reject: item.reason_for_reject,
        status: item.status
      }))
    }
    submitMutation.mutate(dataToSend);
  };

  // Group items by product name for display
  const groupedItems = () => {
    const selectedCustomer = initialData.find(c => c.id === selectedTab);
    if (!selectedCustomer) return [];

    return selectedCustomer.product_detail.map(product => {
      const productId = product.id.toString();
      const statusEntry = formValues.status.find(s => s.id === productId);

      return {
        type: product.name,
        currency: product.currency,
        quantity: product.quantity,
        productId,
        status: statusEntry?.status || "Accept",
        reasonForReject: statusEntry?.reason_for_reject || ""
      };
    });
  };

  // Get all product IDs for a given product type
  const getProductIdsByType = (productType: string): string[] => {
    return formValues.status
      .map((item, index) => ({ item, index }))
      .filter(({ item }) => item.id.includes(productType))
      .map(({ item }) => item.id);
  };
  if(submitMutation.isLoading)  return <Spinner />;

  return (
    <FormProvider {...methods}>
      <div className="fixed h-full inset-0 flex items-center justify-center bg-black-900 bg-opacity-60 animate-overlayShow z-50">
        <div className="bg-white-A700 rounded-xl w-full max-w-xl shadow-lg">
            <div className="flex justify-between p-4 items-start">
              <div>
                <h2 className="text-xl font-semibold">Confirm Booking</h2>
                <p className="text-sm text-gray-500">
                  Accept or reject orders for each customer.
                </p>
              </div>
              <button 
                onClick={onClose}
                disabled={submitMutation.isLoading}
              >
                <X className="h-5 w-5 text-gray-500" />
              </button>
            </div>
          <div className="overflow-y-auto h-[600px] p-8">
            {/* Header */}

            {/* Tabs */}
            <div className="flex space-x-6 border-b border-gray-200 mb-4">
              {initialData.map((customer) => (
                <button
                  key={customer.id}
                  onClick={() => setSelectedTab(customer.id)}
                  className={`text-sm pb-2 border-b-2 transition ${selectedTab === customer.id
                      ? "border-blue-600 text-blue-600 font-semibold"
                      : "text-gray-500 border-transparent"
                    }`}
                  disabled={submitMutation.isLoading}
                >
                  {customer.name}
                </button>
              ))}
            </div>

            {/* Accept All */}
            <div className="bg-[#F1F6FD] p-3 rounded-xl flex justify-between items-center mb-6">
              <span className="text-sm">Do you want to mark all as accepted?</span>
              <button
                onClick={markAllAccepted}
                className="bg-[#165FE3] text-[#fff] text-sm px-4 py-1.5 rounded-full font-medium"
                disabled={submitMutation.isLoading}
              >
                YES
              </button>
            </div>

            <div className="bg-[#FBFBFB]">
              {/* Grouped Product List */}
              {groupedItems().map((item) => (
                <div key={item.productId} className="mb-6">
                  <div className="flex justify-between items-center mb-3">
                    <div>
                      <div className="text-sm font-medium">{item.type}</div>
                      <div className="text-xs text-gray-500">
                        {item.currency} - Qty: {item.quantity}
                      </div>
                    </div>
                    <div className="flex border rounded-md overflow-hidden">
                      <button
                        type="button"
                        onClick={() => handleStatusChange(item.productId, "Accept")}
                        className={`px-4 py-1.5 text-sm border-r ${item.status === "Accept"
                            ? "bg-[#165FE3] text-[#fff]"
                            : "text-[#165FE3]"
                          }`}
                        disabled={submitMutation.isLoading}
                      >
                        Accept
                      </button>
                      <button
                        type="button"
                        onClick={() => handleStatusChange(item.productId, "Reject")}
                        className={`px-4 py-1.5 text-sm ${item.status === "Reject"
                            ? "bg-red-600 text-[#fff]"
                            : "text-red-600"
                          }`}
                        disabled={submitMutation.isLoading}
                      >
                        Reject
                      </button>
                    </div>
                  </div>
                </div>
              ))}

              {/* Reason inputs for rejected products */}
              {selectedCustomerRejectedGroups.map((productType) => {
                const productIds = getProductIdsByType(productType);
                const firstProduct = formValues.status.find(s => s.id.includes(productType));
                const filterOutOthers = reasonOptions.filter(tag => tag !== "Others");
                const productName = firstProduct?.name || "";
                // is the other reason selected
                const isOtherSelected = filterOutOthers.some(tag => firstProduct?.reason_for_reject === tag);                              
                return (
                  <div key={`reason-${productType}`} className="bg-white mt-2 ml-2 p-2">
                    <p className="text-sm font-medium mb-1 text-gray-700">
                      {productName} - Rejection Reason
                    </p>
                    <div className="flex gap-2 flex-wrap mb-2">
                      {reasonOptions.map((tag, i) => (
                        <button
                          type="button"
                          key={i}
                          onClick={() => handleReasonTag(productIds, tag)}
                          className={`text-sm px-3 py-1.5 rounded-full border ${firstProduct?.reason_for_reject === tag
                              ? "border-blue-600 text-blue-600"
                              : "border-gray-300 text-gray-600"
                            }`}
                          disabled={submitMutation.isLoading}
                        >
                          {tag}
                        </button>
                      ))}
                    </div>
                    {
                      // Show text area if other reason is selected
                      !isOtherSelected &&                                        
                      <textarea
                        placeholder="Reason"
                        value={firstProduct?.reason_for_reject}
                        onChange={(e) => handleReasonText(productIds, e.target.value)}
                        rows={3}
                        className="w-full border rounded-xl text-sm px-4 py-2 resize-none"
                        disabled={submitMutation.isLoading}
                      />
                    }
                  </div>
                );
              })}

              {/* Show reason options example (could be removed if not needed) */}
              {/* {rejectedGroups.size === 0 && (
                <div className="bg-white mt-2">
                  <div className="flex gap-2 flex-wrap mb-2 py-8">
                    {reasonOptions.map((tag, i) => (
                      <button
                        key={i}
                        type="button"
                        className="text-sm px-3 py-1.5 rounded-full border border-gray-300 text-gray-600"
                        disabled={submitMutation.isLoading}
                      >
                        {tag}
                      </button>
                    ))}
                  </div>
                  <p className="text-sm font-medium mb-1 text-[#666666] pb-3">
                    Reason
                  </p>
                  <textarea
                    placeholder="Reason"
                    value="Reject items to provide reason"
                    rows={3}
                    className="w-full border rounded-xl text-sm px-4 py-2 resize-none"
                    readOnly
                    disabled={submitMutation.isLoading}
                  />
                </div>
              )} */}
            </div>

            {/* Error message display */}
            {submitMutation.isError && (
              <div className="mt-4 p-3 bg-red-50 text-red-700 rounded-lg text-sm">
                Failed to submit booking confirmation. Please try again.
              </div>
            )}

            {/* Footer Buttons */}
          </div>
          <div className="flex justify-left gap-3 p-4 border-t border-gray-200">
            <Button
              type="button"
              variant="outline"
              className="text-[#165FE3] border-[#165FE3]"
              onClick={onClose}
              disabled={submitMutation.isLoading}
            >
              Cancel
            </Button>
            <Button
              type="button"
              className="bg-[#165FE3] text-[#fff] flex items-center gap-2"
              onClick={handleSubmit(onFormSubmit)}
              disabled={submitMutation.isLoading}
            >
              {submitMutation.isLoading ? (
                <>
                  <span className="w-4 h-4">
                    <Spinner size="sm" />
                  </span>
                  Submitting...
                </>
              ) : (
                "Submit"
              )}
            </Button>
          </div>
        </div>
      </div>
    </FormProvider>
  );
};

export default ConfirmBookingModal;